import { useMemo, useState } from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { kgToTrees, metersToKm, tripEmission } from "libraries/emission";
import TripEmissionChipTree from "components/trips/TripEmissionChipTree";
import TripEmissionChipTravel from "components/trips/TripEmissionChipTravel";
import { TripState } from "types/user";
import { CarState } from "types/cars";
import { TravelMode } from "types/map";
import { useAppSelector } from "redux/hooks";
import {
  selectTripTravelMode,
  selectReferenceElectricGridCountry,
} from "redux/slices/shed/shedSlice";

type DisclaimerProps = {
  trip: TripState;
  tripCar: CarState | null;
  activityKgEmission?: number;
  flightKgEmission?: number;
  extraTransportKgEmission?: number;
};

type Props = {
  trip: TripState;
  tripCar: CarState | null;
  extended?: boolean;
  /** Compact muted copy for Trip Planner panel footer. */
  disclaimer?: boolean;
  /** Hotel/food ADEME activity kg included in Trip Planner totals. */
  activityKgEmission?: number;
  /** Short-haul flight kg included in Trip Planner totals. */
  flightKgEmission?: number;
  /** Transit + walk/bike avoided kg included in Trip Planner totals. */
  extraTransportKgEmission?: number;
};

const KG_CO2_PER_TREE_YEAR = 25;

const formatTripKM = ({ distanceMeters, commuteFrequency }: TripState) =>
  `${Math.round(metersToKm(distanceMeters * commuteFrequency))} KM`;

const formatEmissionFactor = (tripCar: CarState | null): string | null => {
  if (!tripCar?.emissionCO2) {
    return null;
  }

  if (tripCar.emissionType === "electric") {
    return `${tripCar.emissionCO2} Wh/km`;
  }

  return `${tripCar.emissionCO2} g CO₂/km`;
};

const useTripEmissionDisclaimerText = ({
  trip,
  tripCar,
  activityKgEmission = 0,
  flightKgEmission = 0,
  extraTransportKgEmission = 0,
}: DisclaimerProps): string => {
  const tripTravelMode = useAppSelector(selectTripTravelMode);
  const gridCountry = useAppSelector(selectReferenceElectricGridCountry);

  return useMemo(() => {
    const { totalKgEmission, totalTrees } = tripEmission(
      tripCar,
      trip.distanceMeters,
      { gridCountry }
    );
    const hotelFoodKg = Math.max(0, activityKgEmission);
    const flightKg = Math.max(0, flightKgEmission);
    const extraTransportKg = extraTransportKgEmission;
    const activityKg = hotelFoodKg + flightKg + extraTransportKg;
    const activityTrees = kgToTrees(activityKg);
    const totalKgCo2 =
      (totalKgEmission + activityKg) * trip.commuteFrequency;
    const treeCount =
      (totalTrees + activityTrees) * trip.commuteFrequency;
    const distanceKm = metersToKm(trip.distanceMeters * trip.commuteFrequency);
    const drivingKgCo2 = totalKgEmission * trip.commuteFrequency;
    const co2PerKm = distanceKm > 0 ? (drivingKgCo2 * 1000) / distanceKm : 0;
    const emissionFactor = formatEmissionFactor(tripCar);
    const tripKM = formatTripKM(trip);
    const tripCO2 = `${totalKgCo2.toFixed(1)} KG CO2`;
    const tripCount = trip.commuteFrequency || 1;
    const activityNote =
      hotelFoodKg > 0
        ? ` Hotel/meal estimates (ADEME averages: ~10.5 kg/night, ~5.0 kg/meal) add ${hotelFoodKg.toFixed(
            1
          )} kg CO₂ and are included in the tree total.`
        : "";
    const extraTransportNote =
      extraTransportKg !== 0
        ? ` Walk and bike hops count as avoided car CO₂ (negative vs the selected vehicle). Transit (~12.8 g/km) and walk/bike savings together add ${extraTransportKg.toFixed(
            1
          )} kg CO₂ to the trip total (negative means savings).`
        : "";
    const flightNote =
      flightKg > 0
        ? ` Flight estimates (short-haul ~0.246 kg CO₂/km, including a 9% detour) add ${flightKg.toFixed(
            1
          )} kg CO₂ and are included in the tree total. g CO₂/km is driving only.`
        : "";
    const drivingDisclaimer =
      tripTravelMode === TravelMode.DRIVING
        ? ` Emission intensity ${co2PerKm.toFixed(0)} g CO₂/km${
            emissionFactor ? ` · vehicle factor ${emissionFactor}` : ""
          }. Each protected tree absorbs ~${KG_CO2_PER_TREE_YEAR} kg CO₂ annually — this trip equals ${treeCount.toFixed(
            2
          )} trees/year.`
        : "";
    const summaryByMode =
      tripTravelMode === TravelMode.WALKING
        ? `${tripCount} walk(s) (${tripKM}) save ${tripCO2} vs car — about ${treeCount.toFixed(
            2
          )} rainforest trees of yearly absorption.`
        : tripTravelMode === TravelMode.BICYCLING
          ? `${tripCount} ride(s) (${tripKM}) save ${tripCO2} vs car — about ${treeCount.toFixed(
              2
            )} rainforest trees of yearly absorption.`
          : tripTravelMode === TravelMode.DIGITAL
            ? `${tripCount} digital event(s) (${tripKM} avoided) save ${tripCO2} vs car — about ${treeCount.toFixed(
                2
              )} rainforest trees of yearly absorption.`
            : `${tripCount} trip(s) (${tripKM}) emit ${tripCO2}, equal to what ${treeCount.toFixed(
                2
              )} rainforest trees absorb yearly.`;

    return `${summaryByMode}${drivingDisclaimer}${activityNote}${flightNote}${extraTransportNote}`;
  }, [
    activityKgEmission,
    extraTransportKgEmission,
    flightKgEmission,
    gridCountry,
    trip,
    tripCar,
    tripTravelMode,
  ]);
};

/** Clickable info control for the trip-planner rainforest widget. */
export const TripEmissionDisclaimerInfoButton = (props: DisclaimerProps) => {
  const [open, setOpen] = useState(false);
  const disclaimerText = useTripEmissionDisclaimerText(props);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box component="span" sx={{ display: "inline-flex" }}>
        <Tooltip
          arrow
          open={open}
          onClose={() => setOpen(false)}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          placement="top-end"
          title={
            <Typography
              component="p"
              variant="caption"
              sx={{ m: 0, maxWidth: 280, lineHeight: 1.4 }}
            >
              {disclaimerText}
            </Typography>
          }
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: "grey.900",
                px: 1.25,
                py: 1,
                maxWidth: 300,
              },
            },
          }}
        >
          <IconButton
            aria-label="How this CO₂ estimate is calculated"
            aria-expanded={open}
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setOpen((current) => !current);
            }}
            onMouseDown={(event) => event.stopPropagation()}
            sx={{
              color: "common.white",
              bgcolor: "rgba(255,255,255,0.16)",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.28)",
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
};

const TripEmissionDetails = ({
  trip,
  tripCar,
  extended = false,
  disclaimer = false,
  activityKgEmission = 0,
  flightKgEmission = 0,
  extraTransportKgEmission = 0,
}: Props) => {
  const tripTravelMode = useAppSelector(selectTripTravelMode);
  const gridCountry = useAppSelector(selectReferenceElectricGridCountry);
  const disclaimerText = useTripEmissionDisclaimerText({
    trip,
    tripCar,
    activityKgEmission,
    flightKgEmission,
    extraTransportKgEmission,
  });

  const { totalKgEmission, totalTrees } = tripEmission(
    tripCar,
    trip.distanceMeters,
    { gridCountry }
  );
  const hotelFoodKg = disclaimer ? Math.max(0, activityKgEmission) : 0;
  const flightKg = disclaimer ? Math.max(0, flightKgEmission) : 0;
  const extraTransportKg = disclaimer ? extraTransportKgEmission : 0;
  const activityKg = hotelFoodKg + flightKg + extraTransportKg;
  const activityTrees = kgToTrees(activityKg);
  const totalKgCo2 =
    (totalKgEmission + activityKg) * trip.commuteFrequency;
  const treeCount =
    (totalTrees + activityTrees) * trip.commuteFrequency;
  const distanceKm = metersToKm(trip.distanceMeters * trip.commuteFrequency);
  const drivingKgCo2 = totalKgEmission * trip.commuteFrequency;
  const co2PerKm = distanceKm > 0 ? (drivingKgCo2 * 1000) / distanceKm : 0;
  const emissionFactor = formatEmissionFactor(tripCar);
  const tripKM = formatTripKM(trip);
  const tripCO2 = `${totalKgCo2.toFixed(1)} KG CO2`;

  if (disclaimer) {
    return (
      <Typography
        component="p"
        variant="caption"
        color="text.disabled"
        sx={{
          m: 0,
          lineHeight: 1.35,
          fontSize: "0.68rem",
        }}
      >
        {disclaimerText}
      </Typography>
    );
  }

  const extendedDetails =
    extended && tripTravelMode === TravelMode.DRIVING ? (
      <Stack
        spacing={0.75}
        sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}
      >
        <Typography color="text.secondary" variant="body2" component="div">
          Emission intensity:{" "}
          <Typography
            component="span"
            variant="body2"
            color="text.primary"
            fontWeight={600}
          >
            {co2PerKm.toFixed(0)} g CO₂/km
          </Typography>
          {emissionFactor && (
            <>
              {" "}
              · vehicle factor {emissionFactor}
            </>
          )}
        </Typography>
        <Typography color="text.secondary" variant="body2" component="div">
          Rainforest equivalent: each protected tree absorbs ~
          {KG_CO2_PER_TREE_YEAR} kg CO₂ annually — this trip equals{" "}
          {treeCount.toFixed(2)} trees/year
        </Typography>
      </Stack>
    ) : null;

  const summary =
    tripTravelMode === TravelMode.WALKING ? (
      <Typography color="text.secondary" variant="body2" component="div">
        <TripEmissionChipTravel travelMode={tripTravelMode} trip={trip} />
        walk(s) ({tripKM}) saves the environment for {tripCO2} compared to using a
        car. By walking you save CO2 equivalent being the same as{" "}
        <TripEmissionChipTree trip={trip} tripCar={tripCar} />
        rainforest trees absorb yearly
      </Typography>
    ) : tripTravelMode === TravelMode.BICYCLING ? (
      <Typography color="text.secondary" variant="body2" component="div">
        <TripEmissionChipTravel travelMode={tripTravelMode} trip={trip} />
        ride(s) ({tripKM}) saves the environment for {tripCO2} compared to using
        your own car. By biking you save CO2 equivalent being the same as
        <TripEmissionChipTree trip={trip} tripCar={tripCar} />
        rainforest trees absorb yearly
      </Typography>
    ) : tripTravelMode === TravelMode.DIGITAL ? (
      <Typography color="text.secondary" variant="body2" component="div">
        <TripEmissionChipTravel travelMode={tripTravelMode} trip={trip} />
        digital event(s) allow you to travel ({tripKM}) less and saves the
        environment for {tripCO2} compared to using a car. By doing a digital
        event you save CO2 equivalent being the same as
        <TripEmissionChipTree trip={trip} tripCar={tripCar} />
        rainforest trees absorb yearly
      </Typography>
    ) : (
      <Typography color="text.secondary" variant="body2" component="div">
        <TripEmissionChipTravel travelMode={tripTravelMode} trip={trip} />
        trip(s) ({tripKM}) emit {tripCO2}, being the same as what
        <TripEmissionChipTree trip={trip} tripCar={tripCar} />
        rainforest trees absorb yearly
      </Typography>
    );

  return (
    <Stack component="div" spacing={0}>
      {summary}
      {extendedDetails}
    </Stack>
  );
};

export default TripEmissionDetails;
